import type { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useMe } from '@/api/hooks';
import { AppShell } from '@/components/AppShell';
import { Home } from '@/pages/Home';
import { Clients } from '@/pages/Clients';
import { NewClient } from '@/pages/NewClient';
import { ClientEdit } from '@/pages/ClientEdit';
import { Engagements } from '@/pages/Engagements';
import { NewEngagement } from '@/pages/NewEngagement';
import { EngagementEdit } from '@/pages/EngagementEdit';
import { EngagementAdditional, ClientAdditional } from '@/pages/AdditionalDetails';
import { Pending } from '@/pages/Pending';
import { Completed } from '@/pages/Completed';
import { DownloadPending } from '@/pages/DownloadPending';
import { Users } from '@/pages/Users';
import { TerminationRequest } from '@/pages/TerminationRequest';
import { ReactivationRequest } from '@/pages/ReactivationRequest';
import { DiscontinuationRequest } from '@/pages/DiscontinuationRequest';
import { MassChangeRequest } from '@/pages/MassChangeRequest';
import { Help } from '@/pages/Help';
import { SubmittedFormView } from '@/pages/SubmittedFormView';
import { RequestsList } from '@/pages/RequestsList';
import { Settings } from '@/pages/Settings';

// SS9: admin-only routes (Requests, Submitted Forms, Settings) are guarded so a
// non-admin can't reach them by typing the URL, not just hidden in the nav.
function RequireAdmin({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  if (isLoading) return null;
  const isAdmin = !!me?.isSuperAdmin || (me?.roles ?? []).includes('Admin');
  return isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/new" element={<NewClient />} />
        <Route path="/clients/:clientId/:subId" element={<ClientEdit />} />
        <Route path="/clients/:clientId/:subId/additional" element={<ClientAdditional />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route path="/engagements/new" element={<NewEngagement />} />
        <Route path="/engagements/:id" element={<EngagementEdit />} />
        <Route path="/engagements/:id/additional" element={<EngagementAdditional />} />
        {/* SS9: Requests are admin-only. */}
        <Route path="/pending" element={<RequireAdmin><Pending /></RequireAdmin>} />
        <Route path="/pending/download" element={<RequireAdmin><DownloadPending /></RequireAdmin>} />
        <Route path="/completed" element={<RequireAdmin><Completed /></RequireAdmin>} />
        {/* Forms: each request form is its own page/route (CAD Cleanup SS8). The forms
            are open to all; only Submitted Forms is admin-only. */}
        <Route path="/forms" element={<Navigate to="/forms/termination" replace />} />
        <Route path="/forms/termination" element={<TerminationRequest />} />
        <Route path="/forms/discontinuation" element={<DiscontinuationRequest />} />
        <Route path="/forms/reactivation" element={<ReactivationRequest />} />
        <Route path="/forms/mass-change" element={<MassChangeRequest />} />
        <Route path="/forms/submitted" element={<RequireAdmin><RequestsList /></RequireAdmin>} />
        <Route path="/forms/submitted/:id" element={<RequireAdmin><SubmittedFormView /></RequireAdmin>} />
        <Route path="/help" element={<Help />} />
        <Route path="/settings" element={<RequireAdmin><Settings /></RequireAdmin>} />
        <Route path="/users" element={<Users />} />
        <Route path="*" element={<div className="p-6">Page not found.</div>} />
      </Route>
    </Routes>
  );
}
